<script setup>
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { computed, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

import WootMessageEditor from 'components/widgets/WootWriter/Editor.vue';
import Button from 'next/button/Button.vue';

const { t } = useI18n();
const store = useStore();
const route = useRoute();

const uiFlags = useMapGetter('wgptBotKnowledgeManager/getUIFlags');

const inputValue = ref('');

const botId = computed(() => Number(route.params.botId));
const editorStateId = computed(() => {
  return `wgpt-bot-knowledge-manager-chat-editor-${botId.value}`;
});

const sendMessage = async () => {
  try {
    const content = inputValue.value && inputValue.value.trim();
    if (!content) return;

    inputValue.value = '';

    await store.dispatch('wgptBotKnowledgeManager/chat', {
      botId: botId.value,
      content,
    });
  } catch (error) {
    useAlert(error.message);
  }
};

const keyboardEvents = {
  '$mod+Enter': {
    action: () => sendMessage(),
    allowOnFocusedInput: true,
  },
};

useKeyboardEvents(keyboardEvents);
</script>

<template>
  <div class="relative m-2 rounded-xl border border-n-weak bg-n-solid-1">
    <div
      v-if="uiFlags.isSending"
      class="absolute flex items-center w-full h-0 -top-7"
    >
      <div
        class="flex py-2 pr-4 pl-5 shadow-md rounded-full bg-white dark:bg-n-solid-3 text-n-slate-11 text-xs font-semibold my-2.5 mx-auto"
      >
        {{ t('WGPT_BOTS.EDIT.TABS.KNOWLEDGE_MANAGER.CHAT_SECTION.WORKING') }}
        <img
          class="w-6 ltr:ml-2 rtl:mr-2"
          src="assets/images/typing.gif"
          alt="Someone is typing"
        />
      </div>
    </div>
    <template v-if="uiFlags.isAvailable">
      <div class="w-full px-4 min-h-11">
        <WootMessageEditor
          v-if="!uiFlags.isFetching"
          v-model="inputValue"
          :editor-id="editorStateId"
          class="input popover-prosemirror-menu [&_.ProseMirror-woot-style]:min-h-[2rem]"
          dir="auto"
          :placeholder="
            t(
              'WGPT_BOTS.EDIT.TABS.KNOWLEDGE_MANAGER.CHAT_SECTION.INPUT_PLACEHOLDER'
            )
          "
          :min-height="1"
        />
      </div>
      <div class="flex justify-end p-3">
        <Button
          :label="
            t('WGPT_BOTS.EDIT.TABS.KNOWLEDGE_MANAGER.CHAT_SECTION.SEND_BUTTON')
          "
          type="submit"
          sm
          color="blue"
          :disabled="!inputValue || uiFlags.isSending"
          class="flex-shrink-0"
          @click="sendMessage"
        />
      </div>
    </template>
    <div v-else class="text-center text-sm text-n-slate-8 p-4">
      {{ t('WGPT_BOTS.EDIT.TABS.KNOWLEDGE_MANAGER.CHAT_SECTION.UNAVAILABLE') }}
    </div>
  </div>
</template>
